import React, { useMemo, useState } from "react";
import "./FAQ.css";
import faqData from "./faqData.json";
import { Helmet } from "react-helmet-async";

import { SITE_URL } from "../../utils/siteSeo.mjs";

function FAQ() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [openKey, setOpenKey] = useState(null); // one-open accordion

  const categories = useMemo(() => faqData.categories ?? [], []);

  const allItems = useMemo(() => {
    return categories.flatMap((c) =>
      (c.items || []).map((it, idx) => ({
        key: `${c.id}:${idx}`,
        categoryId: c.id,
        categoryTitle: c.title,
        q: it.q,
        a: it.a,
      }))
    );
  }, [categories]);

  const visibleItems = useMemo(() => {
    if (activeCategory === "all") return allItems;
    return allItems.filter((it) => it.categoryId === activeCategory);
  }, [allItems, activeCategory]);

  const counts = useMemo(() => {
    const map = { all: allItems.length };
    for (const c of categories) {
      map[c.id] = allItems.filter((x) => x.categoryId === c.id).length;
    }
    return map;
  }, [allItems, categories]);

  function toggleItem(key) {
    setOpenKey((prev) => (prev === key ? null : key));
  }

  return (
    <section className="faq-hero">
      <Helmet>
        <title>Range Pocket FAQ | Records, Features & Platforms</title>
        <meta
          name="description"
          content="Answers about Range Pocket records, firearm inventory tracking, training tools, supported platforms, subscriptions, and support."
        />
        <link rel="canonical" href={`${SITE_URL}/faq`} />
        <meta property="og:title" content="Range Pocket FAQ | Records, Features & Platforms" />
        <meta
          property="og:description"
          content="Quick answers about Range Pocket records, features, supported platforms, subscriptions, and support."
        />
        <meta property="og:url" content={`${SITE_URL}/faq`} />
        <meta property="og:image" content={`${SITE_URL}/features/feature-armory-status.webp`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: allItems.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          })}
        </script>
      </Helmet>

      <h1 className="faq-title">{faqData.pageTitle || "FAQ"}</h1>
      <p className="faq-tagline">{faqData.tagline}</p>

      <section className="faq-layout">
        {/* Sidebar categories (no chips, no clustering) */}
        <nav className="faq-categories" aria-label="FAQ Categories">
          <div className="categories-title">Categories</div>

          <button
            className={`cat-btn ${activeCategory === "all" ? "active" : ""}`}
            type="button"
            onClick={() => {
              setActiveCategory("all");
              setOpenKey(null);
            }}
          >
            <span className="cat-name">All</span>
            <span className="cat-count">{counts.all}</span>
          </button>

          {categories.map((c) => (
            <button
              key={c.id}
              className={`cat-btn ${activeCategory === c.id ? "active" : ""}`}
              type="button"
              onClick={() => {
                setActiveCategory(c.id);
                setOpenKey(null);
              }}
            >
              <span className="cat-name">{c.title}</span>
              <span className="cat-count">{counts[c.id] || 0}</span>
            </button>
          ))}

          <div className="categories-hint">
            Pick a category to narrow the questions.
          </div>
        </nav>

        {/* Q/A list */}
        <section className="faq-content">
          <div className="faq-list">
            {visibleItems.map((it) => {
              const isOpen = openKey === it.key;
              return (
                <article key={it.key} className="faq-card">
                  <button
                    className="faq-q"
                    onClick={() => toggleItem(it.key)}
                    aria-expanded={isOpen}
                    type="button"
                  >
                    <span className="q-text">{it.q}</span>
                    <span className={`chev ${isOpen ? "open" : ""}`}>⌄</span>
                  </button>

                  {isOpen ? (
                    <div className="faq-a">
                      <div className="a-category">{it.categoryTitle}</div>
                      <p className="a-text">{it.a}</p>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      </section>
    </section>
  );
}

export const FAQ_Page = {
  path: "/faq",
  element: <FAQ></FAQ>,
};

export default FAQ;
